import at from 'constants/ActionTypes/MeshBot';
import {
    METHODS,
    VALUE_TYPES,
    USER_CODE_ENTERED,
    INVALID_USER_CODE_ENTERED,
    INVALID_USER_ID,
    LOCK,
    UNLOCK,
} from 'constants/MeshbotConstant';
import {
    EZLOGIC_TITLE_A_PIN_CODE_IS_ENTERED,
    EZLOGIC_TITLE_INCORRECT_PIN_CODE_IS_ENTERED,
    EZLOGIC_TITLE_LOCKED,
    EZLOGIC_TITLE_UNLOCKED,
    EZLOGIC_TITLE_PIN_CODE_USED,
} from 'constants/language_tokens';

import UserLockOperationFieldsBlock from 'components/DeviceComparisonBlock/components/UserLockOperationFieldsBlock';

import { generateUserLockOperationWhenBlock } from './generateUserLockOperationWhenBlock';
import { getUserLockOperationDynamicFields } from './getUserLockOperationDynamicFields';
import { isUserLockOperationTriggerValid } from './isUserLockOperationTriggerValid';

/**
 * Single data-flow map for non-comparison device capabilities (those with "dynamic fields" instead of a comparator).
 * ONE object per METHOD: method/node and the shared toApi/toUi/validate live once. Per-capability differences live
 * in the `valueTypes` map, keyed by value type.
 *
 * Each entry:
 *   method     — API method (blockOptions.method.name).
 *   node       — meshbot node this method belongs to (currently always at.DEVICE). Used for classification.
 *   toApi      — UI → API: builds the when-block from the ruleTrigger.
 *   toUi       — API → UI: builds capabilityDynamicFields from the saved block's `fields`.
 *   validate   — (block) => boolean: Save-button gate for the built when-block; fetches any data it needs itself
 *                (e.g. controller items) so it isn't passed data it may not use. Optional.
 *   valueTypes — map keyed by capability value type → its behavior:
 *       Component       — the sub-block rendered for this capability (receives `actionOptions` as a prop).
 *       displayName     — optional lang-token for the capability dropdown name (falls back to the item name).
 *       hideCurrentValue — optional; hide the "Current value" line in the capability dropdown.
 *       actionOptions   — [{ value, label, requiresPin, userField? }]: available "state" actions; `label` is a
 *                         lang-token (translated at render); `requiresPin` — needs a PIN (user_codes key);
 *                         `userField` — fixed `user` field value for actions that need no PIN.
 *
 * Extend: add a value type to an existing method's `valueTypes` map, or add a new method object. Everything for a
 * capability — method, node, data flow (toApi/toUi), validation, actions, name, and the render Component — is here.
 */
export const CAPABILITY_DYNAMIC_FIELDS_CONFIG = [
    {
        method: METHODS.IS_USER_LOCK_OPERATION,
        node: at.DEVICE,
        toApi: generateUserLockOperationWhenBlock,
        toUi: getUserLockOperationDynamicFields,
        validate: isUserLockOperationTriggerValid,
        valueTypes: {
            [VALUE_TYPES.USER_CODE_ACTION]: {
                Component: UserLockOperationFieldsBlock,
                hideCurrentValue: true,
                actionOptions: [
                    { value: USER_CODE_ENTERED, label: EZLOGIC_TITLE_A_PIN_CODE_IS_ENTERED, requiresPin: true },
                    {
                        value: INVALID_USER_CODE_ENTERED,
                        label: EZLOGIC_TITLE_INCORRECT_PIN_CODE_IS_ENTERED,
                        requiresPin: false,
                        userField: INVALID_USER_ID,
                    },
                ],
            },
            [VALUE_TYPES.USER_LOCK_OPERATION]: {
                Component: UserLockOperationFieldsBlock,
                displayName: EZLOGIC_TITLE_PIN_CODE_USED,
                hideCurrentValue: true,
                actionOptions: [
                    { value: LOCK, label: EZLOGIC_TITLE_LOCKED, requiresPin: true },
                    { value: UNLOCK, label: EZLOGIC_TITLE_UNLOCKED, requiresPin: true },
                ],
            },
        },
    },
];

/**
 * Negated-node variant per node type (for "not" blocks). Extend when adding non-device nodes.
 */
export const NODE_TYPE_NOT = {
    [at.DEVICE]: at.DEVICE_NOT,
};

/**
 * Returns the method entry whose `valueTypes` map contains the given capability value type.
 * @param {string} valueType
 * @returns {Object|undefined}
 */
export const getCapabilityConfigByValueType = (valueType) => {
    return CAPABILITY_DYNAMIC_FIELDS_CONFIG.find((entry) => entry.valueTypes[valueType]);
};

/**
 * Returns the method entry for the given method.
 * @param {string} method
 * @returns {Object|undefined}
 */
export const getCapabilityConfigByMethod = (method) => {
    return CAPABILITY_DYNAMIC_FIELDS_CONFIG.find((entry) => entry.method === method);
};

/**
 * Returns the per-capability behavior ({ actionOptions }) for the given value type.
 * @param {string} valueType
 * @returns {Object|undefined}
 */
export const getCapabilityBehaviorByValueType = (valueType) => {
    return getCapabilityConfigByValueType(valueType)?.valueTypes[valueType];
};

/**
 * All capability value types that use dynamic fields (across every method). Computed once from the static config —
 * single source for e.g. the capability whitelist.
 * @type {string[]}
 */
export const CAPABILITY_DYNAMIC_FIELDS_VALUE_TYPES = CAPABILITY_DYNAMIC_FIELDS_CONFIG.flatMap((entry) =>
    Object.keys(entry.valueTypes),
);
